import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Shield as ShieldIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import type { TwoFactorSetup } from '../userManagement/TwoFactorSetupDialog';
import TwoFactorSetupDialog from '../userManagement/TwoFactorSetupDialog';

interface UserProfileTwoFactorSectionProps {
  userName: string;
  userEmail: string;
  twoFactorEnabled: boolean;
  onStatusChange: () => Promise<void>;
}

const UserProfileTwoFactorSection: React.FC<UserProfileTwoFactorSectionProps> = ({
  userName,
  userEmail,
  twoFactorEnabled,
  onStatusChange,
}) => {
  const [enabled, setEnabled] = useState(twoFactorEnabled);
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [pendingEnable, setPendingEnable] = useState(false);
  const [pendingDisable, setPendingDisable] = useState(false);

  useEffect(() => {
    setEnabled(twoFactorEnabled);
  }, [twoFactorEnabled]);

  const loadSetup = useCallback(async (regenerate = false) => {
    setLoadingSetup(true);
    setSetupError(null);
    try {
      const url = regenerate
        ? `${getApiUrl()}/auth/profile/two-factor/regenerate`
        : `${getApiUrl()}/auth/profile/two-factor`;
      const response = regenerate
        ? await axios.post(url)
        : await axios.get(url);
      setSetup(response.data?.data || null);
    } catch (err: any) {
      setSetup(null);
      setSetupError(err.response?.data?.error || 'Não foi possível carregar o QR code.');
    } finally {
      setLoadingSetup(false);
    }
  }, []);

  const handleSwitchChange = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setMessage(null);
    setCode('');
    if (checked) {
      setPendingDisable(false);
      setPendingEnable(true);
      await loadSetup(false);
      return;
    }
    setPendingEnable(false);
    if (!enabled) {
      setPendingDisable(false);
      return;
    }
    setPendingDisable(true);
  };

  const applyTwoFactor = async (wantEnabled: boolean) => {
    if (code.replace(/\D/g, '').length !== 6) {
      setMessage({ text: 'Digite os 6 dígitos do aplicativo autenticador.', severity: 'error' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${getApiUrl()}/auth/profile/two-factor`, {
        two_factor_enabled: wantEnabled,
        code,
      });
      setEnabled(wantEnabled);
      setPendingEnable(false);
      setPendingDisable(false);
      setCode('');
      setMessage({
        text: wantEnabled
          ? 'Autenticação em dois fatores ativada. Nos próximos logins será pedido o código do app.'
          : 'Autenticação em dois fatores desativada.',
        severity: 'success',
      });
      await onStatusChange();
      if (wantEnabled) {
        const refreshed = await axios.get(`${getApiUrl()}/auth/profile/two-factor`);
        setSetup(refreshed.data?.data || null);
      }
    } catch (err: any) {
      setMessage({ text: err.response?.data?.error || 'Não foi possível atualizar o 2FA.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const showSetupPanel = pendingEnable || pendingDisable;

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
        <ShieldIcon sx={{ color: '#0d9488', mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Autenticação em dois fatores
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
            Proteja seu login com Google Authenticator, Microsoft Authenticator, Authy ou similar.
          </Typography>
        </Box>
        <Chip
          size="small"
          label={enabled ? 'Ativo no login' : 'Desativado'}
          sx={{
            fontWeight: 700,
            bgcolor: enabled ? 'rgba(13, 148, 136, 0.1)' : '#f1f5f9',
            color: enabled ? '#0f766e' : '#64748b',
            border: `1px solid ${enabled ? 'rgba(13, 148, 136, 0.35)' : '#e2e8f0'}`,
          }}
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={enabled || pendingEnable || pendingDisable}
            onChange={handleSwitchChange}
            disabled={saving || loadingSetup}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#0d9488' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#0d9488' },
            }}
          />
        }
        label="Exigir código do app autenticador ao fazer login"
      />

      {enabled && !pendingEnable && !pendingDisable && (
        <Button
          size="small"
          sx={{ mt: 1, textTransform: 'none' }}
          onClick={() => void loadSetup(false).then(() => setQrDialogOpen(true))}
        >
          Ver QR code / trocar dispositivo
        </Button>
      )}

      {message && (
        <Alert severity={message.severity} sx={{ mt: 2, borderRadius: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {(showSetupPanel || enabled) && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid rgba(13, 148, 136, 0.2)',
            bgcolor: 'rgba(13, 148, 136, 0.04)',
          }}
        >
          {loadingSetup ? (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} sx={{ color: '#0d9488' }} />
            </Box>
          ) : setupError && pendingEnable ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{setupError}</Alert>
          ) : pendingDisable || setup ? (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'flex-start' } }}>
              {setup && pendingEnable && (
                <Box
                  component="img"
                  src={setup.qr_code_data_url}
                  alt="QR code autenticador"
                  sx={{ width: 140, height: 140, bgcolor: '#fff', borderRadius: 1.5, p: 0.5, border: '1px solid #e2e8f0' }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: '#475569', mb: 1.5 }}>
                  {pendingEnable && !enabled
                    ? 'Escaneie o QR code no app e digite o código de 6 dígitos para confirmar a ativação.'
                    : pendingDisable
                      ? 'Para desativar, informe um código válido do app autenticador.'
                      : enabled
                        ? 'O login já exige o código do app. Para desativar, use o interruptor acima e confirme com um código.'
                        : 'Configure o app com este QR code.'}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Código de 6 dígitos"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
                  sx={{ mb: 1.5, maxWidth: 220 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {pendingEnable && !enabled && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={saving}
                      onClick={() => void applyTwoFactor(true)}
                      sx={{ textTransform: 'none', bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}
                    >
                      {saving ? <CircularProgress size={16} color="inherit" /> : 'Confirmar e ativar'}
                    </Button>
                  )}
                  {pendingDisable && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      disabled={saving}
                      onClick={() => void applyTwoFactor(false)}
                      sx={{ textTransform: 'none' }}
                    >
                      {saving ? <CircularProgress size={16} /> : 'Confirmar desativação'}
                    </Button>
                  )}
                  {pendingEnable && setup && (
                    <>
                      <Button size="small" onClick={() => setQrDialogOpen(true)} sx={{ textTransform: 'none' }}>
                        Ver instruções completas
                      </Button>
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        disabled={loadingSetup}
                        onClick={() => void loadSetup(true)}
                        sx={{ textTransform: 'none' }}
                      >
                        Novo QR code
                      </Button>
                    </>
                  )}
                  {(pendingEnable || pendingDisable) && (
                    <Button
                      size="small"
                      onClick={() => { setPendingEnable(false); setPendingDisable(false); setCode(''); }}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancelar
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      )}

      <TwoFactorSetupDialog
        open={qrDialogOpen}
        userName={userName}
        userEmail={userEmail}
        setup={setup}
        loading={loadingSetup}
        error={setupError}
        twoFactorEnabled={enabled}
        onClose={() => setQrDialogOpen(false)}
        onRegenerate={() => loadSetup(true)}
        closeLabel="Fechar"
      />
    </>
  );
};

export default UserProfileTwoFactorSection;
